import type {MaterialStorage} from '@domain/material-storage'
import type {
  ClassificationMetadataResponse,
  MaterialsProducts,
  ProductsForecastInfo,
  GlobalMaterial,
  LimsMaterial,
  MaterialWithRecipes
} from '@domain/materials'
import {Config} from '@domain/shifthandover'
import {StoppageEvent} from '@hconnect/common/stoppages/types/stoppage.types'
import type {
  AssetsTransitions,
  AssetLink,
  EventSearchResult,
  EquipmentData,
  EquipmentPlain,
  PlantNode,
  EntityConfigDto,
  KpiResponse
} from '@hconnect/common/types'
import {CalendarInfo} from '@hconnect/janus/src/components/DailyEntry/types'
import {
  Comments,
  CumulatedKpisResponse,
  DailyKpiInfo,
  Events,
  CategoriesTreeResponse,
  PssKpisDetails,
  WarningResponse,
  PlantMessage,
  PeriodType
} from '@hconnect/janus/src/types'
import {StoppageCode, StoppageDto} from '@hconnect/janus/src/types/stoppages'
import {UpmPlant} from '@hconnect/upm'
import {type FeatureFlag} from '@hproduce/feature-flags'
import {AssetConfigurationDto} from '@kilnfeature/types'

import type {
  AssetsStandardOperationTimes,
  DefaultAssetsOperationTimes,
  AssetStandardOperationTimeValidationData
} from '../../../modules/assets/types'
import type {Schedule} from '../../../modules/common/types'
import {Constraints} from '../../../modules/common/types/contstraints'
import {Latest} from '../../../modules/common/types/latest'
import {
  FunctionsResponse,
  JanusSpecificKpi,
  KpiNamesResponse,
  LastResultsResponse,
  SensorsResponse,
  UnitsResponse
} from '../../../modules/plant-setup/plant-setup-tabs/kpis/types'
import {GrindingCircuitResponse} from '../../../modules/plant-setup/types'
import {isStorageWithMaterialGuard} from '../../../modules/storages'
import {MockState} from '../../mockState'

import areaConfig from './areaConfig.json'
import kilnConfig from './assetConfig/kilnConfig.json'
import assets from './assets'
import assetsLinks from './assetsLinks.json'
import assetsStandardOperationTimes from './assetStandardOperationTimes.json'
import assetsTransitions from './assetsTransitions.json'
import availableKpiUnits from './availableKpiUnits.json'
import classificationMetadata from './classificationMetadata.json'
import comments from './comments.json'
import constraints from './constraints.json'
import cumulatedDailyEntries from './cumulatedDailyEntries.json'
import dailyEntries from './dailyEntries.json'
import dailyEntriesSaved from './dailyEntriesSaved.json'
import defaultAssetOperationTimes from './defaultAssetOperationTimes.json'
import departmentConfig from './departmentConfig.json'
import events from './events.json'
import featureFlags from './featureFlags.json'
import functions from './functions.json'
import globalMaterials from './globalMaterials.json'
import globalMeasurementTypes from './globalMeasurementTypes.json'
import globalMeasurementTypesCategories from './globalMeasurementTypesCategories.json'
import grindingCircuits from './grindingCircuits.json'
import janusSpecificKpis from './janusSpecificKpis.json'
import kpiNames from './kpiNames.json'
import kpiUnits from './kpiUnits.json'
import lastResults from './lastResults.json'
import latestRaw from './latest.json'
import limsMaterials from './limsMaterials.json'
import limsMeasurementTypes from './limsMeasurementTypes.json'
import limsSamplingPoints from './limsSamplingPoints.json'
import mainEquipments from './mainEquipments.json'
import products from './materialsProducts.json'
import materialStorage from './materialStorage.json'
import materialsWithRecipes from './materialsWithRecipes.json'
import measurementTypesMappings from './measurementTypesMappings.json'
import monthlyCalendarInfo from './monthlyCalendar.json'
import categoriesTree from './monthlyEntriesCategoriesTree.json'
import monthlyResultKpis from './monthlyResultKpis.json'
import plainEquipments from './plainEquipments.json'
import plantMessages from './plantMessages.json'
import plantNode from './plantNode.json'
import processStageKpis from './processStageKpis.json'
import processStages from './processStages.json'
import productsSalesForecastInfo from './productsSalesForecastInfo.json'
import pssCategoriesTree from './pssCategoriesTree.json'
import pssKpis from './pssKpis.json'
import pssKpisWithAuxilary from './pssKpisWithAuxilary.json'
import pssStoppages from './pssStoppages.json'
import refraSettings from './refraSettings.json'
import samplingPoints from './samplingPoints.json'
import samplingPointsCounts from './samplingPointsCounts.json'
import samplingPointTypes from './samplingPointTypes.json'
import schedule from './schedule.json'
import periodStatuses from './selectedPeriodStatus.json'
import sensors from './sensors.json'
import shiftHandoverConfig from './shifthandoverConfig.json'
import specificKpisByDefinition from './specificKpisByDefinition.json'
import standardTimesValidationErrorResponse from './standardTimesValidationErrorResponse.json'
import stoppageCodes from './stoppageCodes.json'
import stoppages from './stoppages.json'
import stoppagesDetails from './stoppagesDetails.json'
import area from './upm/areaDetails.json'
import areaTypes from './upm/areaTypes.json'
import department from './upm/departmentDetails.json'
import departmentTypes from './upm/departmentTypes.json'
import upmEquipment from './upm/equipment.json'
import plantGeneralData from './upm/plantGeneralData.json'
import upmPlants from './upm/plants.json'
import upmRestoreStructure from './upm/restoreStructure.json'
import upmSignals from './upm/signals.json'
import upmSignalType from './upm/signalType.json'
import upmStructure from './upm/structure.json'
import upmUnits from './upm/units.json'
import warnings from './warnings.json'
import weeklyCalendarInfo from './weeklyCalendar.json'

import {GlobalMeasurementType} from '@settings/modules/measurements/types/GlobalMeasurementType'
import {GlobalMeasurementTypesCategory} from '@settings/modules/measurements/types/GlobalMeasurementTypesCategory'
import {LimsMeasurementType} from '@settings/modules/measurements/types/LimsMeasurementType'
import {MeasurementTypesMapping} from '@settings/modules/measurements/types/MeasurementTypesMapping'
import {
  LimsSamplingPoint,
  SamplingPointCounts,
  SamplingPointType,
  SamplingPoint
} from '@settings/modules/sampling-points/types'
import {Equipment} from '@settings/modules/upm/types/equipment'
import {SignalListData} from '@settings/modules/upm/types/signal'

export const mockState: MockState = {
  burglengenfeld: {
    pssKpisWithAux: pssKpisWithAuxilary as PssKpisDetails,
    selectedPeriodStatus: periodStatuses as PeriodType[],
    mainEquipments: mainEquipments as EquipmentData[],
    plainEquipments: plainEquipments as EquipmentPlain[],
    processStages: processStages,
    janusSpecificKpis: janusSpecificKpis as unknown as JanusSpecificKpi[],
    specificKpisByDefinition: specificKpisByDefinition as unknown as JanusSpecificKpi[],
    kpiMonthlyCalendar: monthlyCalendarInfo as CalendarInfo,
    kpiWeeklyCalendar: weeklyCalendarInfo as CalendarInfo,
    kpiDailyComments: comments as unknown as Comments,
    kpiDailyEvents: events as unknown as Events,
    kpiDailyEntries: dailyEntries as unknown as DailyKpiInfo[],
    kpiDailyEntriesSaved: dailyEntriesSaved as unknown as DailyKpiInfo[],
    kpiCumulatedDailyEntries: cumulatedDailyEntries as unknown as CumulatedKpisResponse,
    schedule: schedule as Schedule,
    warnings: warnings as WarningResponse,
    monthlyEntriesCategories: categoriesTree as unknown as CategoriesTreeResponse,
    pssEntriesCategories: pssCategoriesTree as unknown as CategoriesTreeResponse,
    defaultAssetsOperationTimes: defaultAssetOperationTimes as DefaultAssetsOperationTimes,
    assetsStandardOperationTimes: assetsStandardOperationTimes as AssetsStandardOperationTimes,
    materialsWithRecipes: materialsWithRecipes as unknown as MaterialWithRecipes[],
    stoppageDetails: stoppagesDetails as StoppageEvent[],
    stoppageCodes: stoppageCodes as StoppageCode[],
    plantNode: plantNode as unknown as PlantNode,
    materialsProducts: products as MaterialsProducts,
    assets,
    latest: latestRaw as Latest,
    assetsTransitions: assetsTransitions as AssetsTransitions,
    assetsLinks: assetsLinks as AssetLink[],
    recipesById: (materialsWithRecipes as unknown as MaterialWithRecipes[])
      .flatMap((material) => material.recipes)
      .reduce((recipesById, recipe) => ({...recipesById, [recipe.id]: recipe}), {}),
    materialsById: (materialsWithRecipes as unknown as MaterialWithRecipes[]).reduce(
      (materialsById, material) => ({...materialsById, [material.id]: material}),
      {}
    ),
    materialStorage: materialStorage as MaterialStorage[],
    storagesWithMaterial: (materialStorage as MaterialStorage[]).filter(isStorageWithMaterialGuard),
    productsSalesForecastInfo: productsSalesForecastInfo as ProductsForecastInfo,
    kpis: processStageKpis as unknown as KpiResponse,
    pssKpis: pssKpis as PssKpisDetails,
    standardTimesValidationErrorResponse:
      standardTimesValidationErrorResponse as AssetStandardOperationTimeValidationData,
    validatedPostSTDRequests: {},
    validatedPutSTDRequests: {},
    sensors: sensors as SensorsResponse,
    grindingCircuits: grindingCircuits as GrindingCircuitResponse,
    constraints: constraints as Constraints,
    functions: functions as FunctionsResponse,
    departmentConfig: departmentConfig as EntityConfigDto,
    areaConfig: areaConfig as EntityConfigDto,
    shiftHandoverConfig: shiftHandoverConfig as Config,
    stoppages: stoppages as unknown as EventSearchResult,
    pssStoppages: pssStoppages as StoppageDto[],
    kpiUnits: kpiUnits as UnitsResponse,
    kpiNames: kpiNames as KpiNamesResponse,
    availableKpiUnits: availableKpiUnits as UnitsResponse,
    globalMeasurementTypesCategories:
      globalMeasurementTypesCategories as GlobalMeasurementTypesCategory[],
    globalMeasurementTypes: globalMeasurementTypes as GlobalMeasurementType[],
    limsMeasurementTypes: limsMeasurementTypes as LimsMeasurementType[],
    measurementTypesMappings: measurementTypesMappings as MeasurementTypesMapping[],
    samplingPointsTypes: samplingPointTypes as SamplingPointType[],
    limsSamplingPoints: limsSamplingPoints as LimsSamplingPoint[],
    samplingPointsCounts: samplingPointsCounts as SamplingPointCounts[],
    samplingPoints: samplingPoints as SamplingPoint[],
    lastResults: lastResults as LastResultsResponse,
    upm: {
      structure: upmStructure as unknown as PlantNode,
      equipment: upmEquipment,
      units: upmUnits,
      signals: upmSignals as SignalListData[],
      signalType: upmSignalType,
      plantData: plantGeneralData,
      plants: upmPlants as UpmPlant[],
      area,
      departmentTypes,
      areaTypes,
      restoreStructure: upmRestoreStructure as unknown as PlantNode,
      department: department as unknown as Equipment
    },
    globalMaterials: globalMaterials as GlobalMaterial[],
    limsMaterials: limsMaterials as LimsMaterial[],
    monthlyResultKpis: monthlyResultKpis as PssKpisDetails,
    classificationMetadata: classificationMetadata as unknown as ClassificationMetadataResponse,
    plantMessages: plantMessages as PlantMessage[],
    kilnConfig: kilnConfig as unknown as AssetConfigurationDto,
    refraSettings,
    featureFlags: featureFlags as FeatureFlag[]
  }
}
